'use client';

import { resetPassword } from '@aws-amplify/auth';
import { ExclamationCircleIcon } from '@heroicons/react/20/solid';
import { yupResolver } from '@hookform/resolvers/yup';
import { Alert, Button, ConfigProvider, Divider, Input, message } from 'antd';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { theme } from '@/common/theme';
import { ForgotPasswordSchema } from '@/schema/auth/forgotPasswordSchema';
import type { ForgotPasswordType } from '@/types/auth';

const ForgotPassword = () => {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    document.body.classList.add('overflow-hidden');

    return () => {
      document.body.classList.remove('overflow-hidden');
    };
  }, []);

  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm<ForgotPasswordType>({
    criteriaMode: 'all',
    defaultValues: {
      email: '',
    },
    mode: 'onBlur',
    resolver: yupResolver(ForgotPasswordSchema),
  });

  const handleForgotPassword = async (data: ForgotPasswordType) => {
    setIsLoading(true);
    try {
      await resetPassword({
        username: data.email,
      });

      message.success('認証コードを送信しました');
      const query = { email: data.email };
      const queryString = new URLSearchParams(query).toString();
      router.push(`/auth/reset?${queryString}`);
    } catch (error) {
      if (error && typeof error === 'object' && 'code' in error) {
        switch (error.code) {
          case 'CodeMismatchException':
            break;
          case 'LimitExceededException':
            setErrorMessage(
              '更新リクエスト数が多すぎます。後でもう一度お試しください。',
            );
            break;
          case 'ExpiredCodeException':
            break;
          case 'InvalidPasswordException':
            break;
          case 'InvalidParameterException':
            break;
        }
      } else {
        console.error(error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <title>パスワードの再設定 - LIFTI partners（リフティ パートナー）</title>
      <ConfigProvider theme={theme}>
        <div className='flex min-h-screen overflow-hidden'>
          <div className='relative hidden w-0 flex-1 lg:block'>
            <Image
              className='absolute inset-0 size-full object-cover'
              src='/images/login-img.jpeg'
              alt=''
              fill={true}
              quality={85}
            />
          </div>
          <div className='flex flex-1 flex-col justify-center px-4 py-12 sm:px-6 lg:flex-none lg:px-20 xl:px-24'>
            <div className='mx-auto w-full max-w-sm lg:w-96'>
              <div>
                <Image
                  width={180}
                  height={90}
                  className='h-12 w-auto'
                  src='/images/logo/partners-logo.svg'
                  alt='logo'
                />
                <h2 className='mt-6 text-3xl font-bold tracking-tight text-gray-900'>
                  パスワードをリセット
                </h2>
                <p className='py-6 text-sm'>
                  メールアドレスを入力すると、パスワードをリセットするための認証コードが記載されたメールが送信されます。
                </p>
              </div>
              {errorMessage && <Alert type='error' message='' banner />}
              <div className='mt-8'>
                <div className='mt-6'>
                  <form
                    className='space-y-6'
                    onSubmit={handleSubmit(handleForgotPassword)}
                  >
                    <div>
                      <label
                        htmlFor='email'
                        className='block text-sm font-medium text-gray-700'
                      >
                        メールアドレス
                      </label>
                      <div className='relative mt-2'>
                        <div className='rounded-md shadow-sm'>
                          <Controller
                            name='email'
                            control={control}
                            render={({ field }) => (
                              <Input
                                type='email'
                                id='email'
                                {...field}
                                placeholder='example@lifti.jp'
                                status={errors.email && 'error'}
                                size='large'
                              />
                            )}
                          />
                        </div>
                        {errors.email?.message && (
                          <div className='pointer-events-none absolute inset-y-0 bottom-7 right-0 flex items-center pr-3'>
                            <ExclamationCircleIcon
                              className='size-5 text-red-500'
                              aria-hidden='true'
                            />
                          </div>
                        )}
                        <p
                          className='mt-2 text-sm text-red-600'
                          id='company-error'
                        >
                          {errors.email?.message}
                        </p>
                      </div>
                    </div>
                    <Divider />
                    <div>
                      <Button
                        htmlType='submit'
                        type='primary'
                        className='w-full'
                        size='large'
                        loading={isLoading}
                      >
                        続ける
                      </Button>
                      <div className='mt-6 text-center text-sm'>
                        <Link
                          href='/auth/login'
                          className='text-accent no-underline'
                        >
                          ログインに戻る
                        </Link>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </ConfigProvider>
    </>
  );
};

export default ForgotPassword;
